// src/pages/admin/AddItem.js
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import {Box} from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Button as MuiButton } from '@mui/material';
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";

export default function AddItem() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // decide which form we’re in
  const isMaster = location.pathname.includes("master");

  //
  // --- Shared state & dropdowns for Storehouse mode only ---
  //
  const [storehouses, setStorehouses] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [quadrants, setQuadrants] = useState([]);
  const [filters, setFilters] = useState([]);

  useEffect(() => {
    if (!isMaster) {
      axios
        .get(`${process.env.REACT_APP_BASE_URL}common/storehouses`, { headers })
        .then((r) => setStorehouses(r.data))
        .catch(console.error);
      axios
        .get(`${process.env.REACT_APP_BASE_URL}common/tabs`, { headers })
        .then((r) => setTabs(r.data))
        .catch(console.error);
      axios
        .get(`${process.env.REACT_APP_BASE_URL}common/quadrants`, { headers })
        .then((r) => setQuadrants(r.data))
        .catch(console.error);
      axios
        .get(`${process.env.REACT_APP_BASE_URL}common/filters`, { headers })
        .then((r) => setFilters(r.data))
        .catch(console.error);
    }
  }, [isMaster, token]);

  //
  // --- Form state ---
  //
  const [item, setItem] = useState({
    // storehouse fields
    storehouse: "",
    tab: "",
    quadrant: "",
    filter: "",
    locId: "",
    active: true,
    vendor: "",
    easPN: "",
    min: "",
    max: "",
    orderMult: "",
    group: "",
    alwaysCount: false,
    shelf: "",
    // master fields
    itemPN: "",
    description: "",
    issueMult: "",
    uom: "",
    costEach: "",
    convFactor: "",
    vendorPN: "",
    expires: "",
    procNotes: "",
    commodityCode: "",
    defaultCategory: "",
    ccGL: "",
    ccJob: "",
    ccWty: "",
    ccWo: "",
    ccCapex: "",
    glBm: "",
    glEm: "",
    glStr: "",
    indicator: false,
  });

  //
  // --- Submit handler ---
  //
  const handleSubmit = async () => {
    try {
      if (isMaster) {
        // master payload
        const payload = {
          itemPN: item.itemPN,
          description: item.description,
          issueMult: Number(item.issueMult),
          uom: item.uom,
          costEach: Number(item.costEach),
          convFactor: Number(item.convFactor),
          vendorPN: item.vendorPN,
          expires: item.expires,
          procNotes: item.procNotes,
          commodityCode: item.commodityCode,
          defaultCategory: item.defaultCategory,
          CCGL: item.ccGL,
          CC_JOB: item.ccJob,
          CC_WTY: item.ccWty,
          CC_WO: item.ccWo,
          CC_CAPEX: item.ccCapex,
          GL_BM: item.glBm,
          GL_EM: item.glEm,
          GL_STR: item.glStr,
          GL_IND: item.indicator,
        };
        // minimal validation
        if (!payload.itemPN) {
          alert("Item PN is required.");
          return;
        }
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}common/master-items`,
          payload,
          { headers }
        );
        alert("Master item created!");
        navigate("/items-list");
      } else {
        // storehouse payload
        const { storehouse, tab, quadrant, filter, itemPN, locId } = item;
        if (!storehouse || !tab || !quadrant || !filter || !itemPN || !locId) {
          alert("Please fill all required fields.");
          return;
        }
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}common/items`,
          item,
          { headers }
        );
        alert("Storehouse item created!");
        navigate("/items-list");
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || "Error creating record");
    }
  };

  //
  // --- Layout constants ---
  //
  const fieldCols = { xs: 12, md: 3, className: "mb-3" };

  return (
    <PageLayout>
      <Grid container spacing={2} className="align-items-center mb-3">
        <Grid size={{ xs: 12, md: 6 }}>
          <Breadcrumb title={isMaster ? "Add Master Item" : "Add Item"} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} className="text-end">
          <Link to="/items-list">
            <MuiButton variant="contained" color="secondary">
              Back to {isMaster ? "Master List" : "Items List"}
            </MuiButton>
          </Link>
        </Grid>
      </Grid>

      <Box className="mc-card">
        <Grid container spacing={2}>
          {isMaster ? (
            // ---- Master fields ----
            <>
              {[
                ["Item PN", "itemPN"],
                ["Description", "description"],
                ["Issue Mult", "issueMult"],
                ["UOM", "uom"],
                ["Cost Each", "costEach"],
                ["Conv", "convFactor"],
                ["Vendor PN", "vendorPN"],
                ["Expires", "expires"],
                ["Proc Notes", "procNotes"],
                ["Commodity Code", "commodityCode"],
                ["Default Category", "defaultCategory"],
                ["CCGL", "ccGL"],
                ["CC_JOB", "ccJob"],
                ["CC_WTY", "ccWty"],
                ["CC_WO", "ccWo"],
                ["CC_CAPEX", "ccCapex"],
                ["GL_BM", "glBm"],
                ["GL_EM", "glEm"],
                ["GL_STR", "glStr"],
              ].map(([label, field]) => (
                <Grid size={{ xs: 12, md: 3 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    fullWidth
                    value={item[field] || ""}
                    onChange={(e) =>
                      setItem({ ...item, [field]: e.target.value })
                    }
                  />
                </Grid>
              ))}
              <Grid size={{ xs: 12, md: 3 }} className="mb-3">
                <CardHeader title="Indicator" />
                <TextField
                  select
                  fullWidth
                  value={item.indicator.toString()}
                  onChange={(e) =>
                    setItem({ ...item, indicator: e.target.value === "true" })
                  }
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
            </>
          ) : (
            // ---- Storehouse fields ----
            <>
              {[
                {
                  label: "Storehouse",
                  field: "storehouse",
                  options: storehouses,
                },
                {
                  label: "Tab",
                  field: "tab",
                  options: tabs,
                  display: (o) => `${o.name} (${o.index})`,
                },
                {
                  label: "Quadrant",
                  field: "quadrant",
                  options: quadrants,
                  display: (o) => `${o.name} (${o.index})`,
                },
                {
                  label: "Filter",
                  field: "filter",
                  options: filters,
                  display: (o) => `${o.name} (${o.index})`,
                },
              ].map(({ label, field, options, display }) => (
                <Grid size={{ xs: 12, md: 3 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    select
                    fullWidth
                    value={item[field]}
                    onChange={(e) =>
                      setItem({ ...item, [field]: e.target.value })
                    }
                  >
                    {options.map((o) => (
                      <MenuItem key={o._id} value={o._id}>
                        {display ? display(o) : o.code || o.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}

              {[
                ["Item PN", "itemPN"],
                ["Item Name", "itemName"],
                ["LocID", "locId"],
                ["Vendor", "vendor"],
                ["EAS PN", "easPN"],
              ].map(([label, field]) => (
                <Grid size={{ xs: 12, md: 3 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    fullWidth
                    value={item[field] || ""}
                    onChange={(e) =>
                      setItem({ ...item, [field]: e.target.value })
                    }
                  />
                </Grid>
              ))}

              {/* Booleans */}
              <Grid size={{ xs: 12, md: 3 }} className="mb-3">
                <CardHeader title="Always Count" />
                <TextField
                  select
                  fullWidth
                  value={item.alwaysCount.toString()}
                  onChange={(e) =>
                    setItem({ ...item, alwaysCount: e.target.value === "true" })
                  }
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }} className="mb-3">
                <CardHeader title="Active" />
                <TextField
                  select
                  fullWidth
                  value={item.active.toString()}
                  onChange={(e) =>
                    setItem({ ...item, active: e.target.value === "true" })
                  }
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>

              {/* The rest */}
              {[
                ["Min", "min"],
                ["Max", "max"],
                ["Order Mult.", "orderMult"],
                ["Group", "group"],
                ["Shelf", "shelf"],
              ].map(([label, field]) => (
                <Grid size={{ xs: 12, md: 4 }} className="mb-3" key={field}>
                  <CardHeader title={label} />
                  <TextField
                    fullWidth
                    type={
                      ["min", "max", "orderMult"].includes(field)
                        ? "number"
                        : "text"
                    }
                    value={item[field] || ""}
                    onChange={(e) =>
                      setItem({ ...item, [field]: e.target.value })
                    }
                  />
                </Grid>
              ))}
            </>
          )}
        </Grid>

        <Grid container spacing={2} className="mt-3">
          <Grid size={12} className="text-end">
            <MuiButton variant="contained" color="primary" onClick={handleSubmit}>
              {isMaster ? "Create Master Item" : "Create Item"}
            </MuiButton>
          </Grid>
        </Grid>
      </Box>
    </PageLayout>
  );
}
